import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, HelpCircle, Bot, ThumbsUp, ThumbsDown, ArrowDown, Loader2, TrendingUp } from 'lucide-react';
import { useLocation, useNavigate, useSearchParams, Routes, Route, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queriesService, QueryTestResult } from '@/api/queriesService';
import DashboardLlmsTxtPage from './DashboardLlmsTxtPage';
import DashboardQueriesPage from './DashboardQueriesPage';
import QueryDetailModal from '@/components/QueryDetailModal';
import ResultsPaywallOverlay from '@/components/ResultsPaywallOverlay';
import MarkdownRenderer from '@/components/MarkdownRenderer';
import MentionsChart from '@/components/MentionsChart';
import ProviderPerformanceChart from '@/components/ProviderPerformanceChart';
import { LLM_PROVIDER_LOGOS, ALL_LLM_PROVIDERS, LlmProvider } from '@/types/llm-provider';
import ErrorBoundary from '@/components/ErrorBoundary';
import { useAuth } from '@/context/AuthContext';
import { useWebsite } from '@/context/WebsiteContext';
import { Period, PERIOD_LABELS, getPeriodDates } from '@/utils/period';
import GenerateReportButton from '@/components/report/GenerateReportButton';


const DashboardInsightsPage: React.FC = () => {
  const { userSession } = useAuth();
  const { selectedWebsite } = useWebsite();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showReportButton = searchParams.get('report') === 'true';
  const pathname = location.pathname;
  const [timeFilter, setTimeFilter] = useState<Period>('this_week');
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState<QueryTestResult | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const timeFilterOptions: Period[] = ['today', 'this_week', 'this_month', 'last_30_days', 'last_90_days', 'all_time'];

  const { start, end } = useMemo(() => getPeriodDates(timeFilter), [timeFilter]);

  const { data: dashboardData, isLoading } = useQuery({
    queryKey: ['dashboard', start, end, selectedWebsite?.id],
    queryFn: () => queriesService.getDashboardData(start, end, selectedWebsite?.id),
    staleTime: 5 * 60 * 1000,
  });

  const { data: providerAnalytics } = useQuery({
    queryKey: ['provider-analytics', start, end, selectedWebsite?.id],
    queryFn: () => queriesService.getProviderAnalytics(start, end, selectedWebsite?.id),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowTimeDropdown(false);
      }
    };

    if (showTimeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTimeDropdown]);

  const handleTimeFilterChange = (filter: Period) => {
    setTimeFilter(filter);
    setShowTimeDropdown(false);
  };

  const dashboardNavClass = (path: string) => {
    const active = pathname === path;
    return [
      'font-mono font-medium text-xs tracking-[0.6px] uppercase transition-colors',
      active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
    ].join(' ');
  };

  const getQueryLLMs = (query: QueryTestResult) => {
    const mentioned = query.llmResults.filter(r => r.mentioned).map(r => r.llm);
    // All LLMs that aren't in the mentioned list are considered "not mentioned"
    const notMentioned = ALL_LLM_PROVIDERS.filter(llm => !mentioned.includes(llm));
    return { mentioned, notMentioned };
  };

  const handleQueryClick = (query: QueryTestResult) => {
    setSelectedQuery(query);
    setShowDetailModal(true);
  };

  const metrics = dashboardData?.metrics;
  const isSubscribed = dashboardData?.isSubscribed ?? false;
  const realQueries = dashboardData?.recentQueries || [];

  // For free users: multiply the first query 10 times as placeholders
  // First one is visible, rest are blurred
  const displayQueries = isSubscribed
    ? realQueries
    : realQueries.length > 0
    ? Array.from({ length: 10 }, (_, i) => ({
        ...realQueries[0],
        id: i === 0 ? realQueries[0].id : `placeholder-${i}`,
      }))
    : [];

  return isLoading ? (
    <div className="flex items-center justify-center min-h-[400px]">
      <Loader2 className="h-8 w-8 animate-spin text-foreground" />
    </div>
  ) : (
    <div className="flex flex-col gap-5 items-center w-full py-5 px-4 md:px-0">
      <div className="flex flex-col gap-4 items-start w-full max-w-[1080px]">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between w-full gap-3 sm:gap-0">
          <h1 className="font-sans font-[450] leading-[1.5] text-[#020203] text-[20px]" style={{ fontVariationSettings: "'wdth' 100" }}>
            Insights
          </h1>
          <div className="flex items-center justify-end gap-3 relative" ref={dropdownRef}>
            {showReportButton && (
              <GenerateReportButton
                dashboardData={dashboardData}
                providerAnalytics={providerAnalytics}
                websiteDomain={selectedWebsite?.domain ?? ''}
                period={timeFilter}
                periodStart={start}
                periodEnd={end}
              />
            )}
            <button
              onClick={() => setShowTimeDropdown(!showTimeDropdown)}
              className="bg-white border border-[rgba(2,2,3,0.06)] flex gap-3 h-12 items-center justify-center px-3 py-2.5 rounded hover:opacity-80 transition-opacity"
            >
              <div className="flex gap-2 items-center">
                <p className="font-sans font-[450] leading-none text-[#020203] text-base" style={{ fontVariationSettings: "'wdth' 100" }}>
                  {PERIOD_LABELS[timeFilter]}
                </p>
                <ChevronDown className="h-3 w-3 text-[#353535]" />
              </div>
            </button>
            {showTimeDropdown && (
              <div className="absolute top-full right-0 mt-2 bg-white border border-[rgba(2,2,3,0.06)] rounded shadow-lg z-10 min-w-[180px] py-1">
                {timeFilterOptions.map((option) => (
                  <button
                    key={option}
                    onClick={() => handleTimeFilterChange(option)}
                    className={`w-full text-left px-4 py-2.5 font-mono font-medium text-xs tracking-[0.6px] uppercase transition-colors ${
                      timeFilter === option
                        ? 'bg-[rgba(2,2,3,0.04)] text-[#020203]'
                        : 'text-[#020203] hover:bg-[rgba(2,2,3,0.02)]'
                    }`}
                  >
                    {PERIOD_LABELS[option]}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5 w-full">
          <div className="bg-white border border-[rgba(2,2,3,0.06)] flex flex-col gap-3 h-[106px] items-start p-4 md:p-5 rounded">
            <div className="flex items-center justify-between text-[#020203] text-xs uppercase w-full">
              <p className="font-mono font-medium leading-[1.5] opacity-50 tracking-[0.6px]">
                Queries tested
              </p>
              <HelpCircle className="h-3 w-3 opacity-50" />
            </div>
            <div className="flex items-center justify-between w-full">
              <p className="font-condensed font-normal leading-[1.1] text-[#020203] text-[24px] md:text-[28px] uppercase">
                {metrics?.queriesTested || 0}
              </p>
              <div className="bg-white flex items-center justify-center px-2 py-1.5 rounded size-8">
                <p className="font-mono font-medium leading-4 text-xs text-white uppercase">
                  --
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-[rgba(2,2,3,0.06)] flex flex-col gap-3 h-[106px] items-start p-4 md:p-5 rounded">
            <div className="flex items-center justify-between text-[#020203] text-xs uppercase w-full">
              <p className="font-mono font-medium leading-[1.5] opacity-50 tracking-[0.6px]">
                Visibility Score
              </p>
              <Bot className="h-3 w-3 opacity-50" />
            </div>
            <div className="flex items-center justify-between w-full">
              <p className="font-condensed font-normal leading-[1.1] text-[#020203] uppercase">
                <span className="text-[24px] md:text-[28px]">{metrics?.visibilityScore || 0}</span>
                <span className="text-[16px] md:text-[20px]">/100</span>
              </p>
              <div className={`flex items-center justify-center px-2 py-1.5 rounded size-8 ${
                metrics?.visibilityTrend === 'down' ? 'bg-[rgba(225,73,68,0.1)]' :
                metrics?.visibilityTrend === 'up' ? 'bg-[rgba(0,169,68,0.1)]' : 'bg-white'
              }`}>
                <ArrowDown className={`h-3 w-3 ${
                  metrics?.visibilityTrend === 'down' ? 'text-[#e14944]' :
                  metrics?.visibilityTrend === 'up' ? 'text-[#00a944] rotate-180' : 'text-gray-400'
                }`} />
              </div>
            </div>
          </div>

          <div className="bg-white border border-[rgba(2,2,3,0.06)] flex flex-col gap-3 h-[106px] items-start p-4 md:p-5 rounded">
            <div className="flex items-center justify-between text-[#020203] text-xs uppercase w-full">
              <p className="font-mono font-medium leading-[1.5] opacity-50 tracking-[0.6px]">
                Mention
              </p>
              <ThumbsUp className="h-3 w-3 opacity-50" />
            </div>
            <div className="flex items-center justify-between w-full">
              <p className="font-condensed font-normal leading-[1.1] text-[#020203] text-[24px] md:text-[28px] uppercase">
                {metrics?.mentionPercentage || 0}%
              </p>
              <div className={`flex items-center justify-center px-2 py-1.5 rounded size-8 ${
                metrics?.mentionTrend === 'down' ? 'bg-[rgba(225,73,68,0.1)]' :
                metrics?.mentionTrend === 'up' ? 'bg-[rgba(0,169,68,0.1)]' : 'bg-white'
              }`}>
                <ArrowDown className={`h-3 w-3 ${
                  metrics?.mentionTrend === 'down' ? 'text-[#e14944]' :
                  metrics?.mentionTrend === 'up' ? 'text-[#00a944] rotate-180' : 'text-gray-400'
                }`} />
              </div>
            </div>
          </div>

          <div className="bg-white border border-[rgba(2,2,3,0.06)] flex flex-col gap-3 h-[106px] items-start p-4 md:p-5 rounded">
            <div className="flex items-center justify-between text-[#020203] text-xs uppercase w-full">
              <p className="font-mono font-medium leading-[1.5] opacity-50 tracking-[0.6px]">
                No Mention
              </p>
              <ThumbsDown className="h-3 w-3 opacity-50" />
            </div>
            <div className="flex items-center justify-between w-full">
              <p className="font-condensed font-normal leading-[1.1] text-[#020203] text-[24px] md:text-[28px] uppercase">
                {metrics?.noMentionPercentage || 0}%
              </p>
              <div className={`flex items-center justify-center px-2 py-1.5 rounded size-8 ${
                metrics?.noMentionTrend === 'down' ? 'bg-[rgba(225,73,68,0.1)]' :
                metrics?.noMentionTrend === 'up' ? 'bg-[rgba(0,169,68,0.1)]' : 'bg-white'
              }`}>
                <ArrowDown className={`h-3 w-3 ${
                  metrics?.noMentionTrend === 'down' ? 'text-[#e14944]' :
                  metrics?.noMentionTrend === 'up' ? 'text-[#00a944] rotate-180' : 'text-gray-400'
                }`} />
              </div>
            </div>
          </div>
        </div>

        {(dashboardData?.mentionsTimeline?.length ?? 0) >= 2 && (
          <div className="bg-white border border-[rgba(2,2,3,0.06)] flex flex-col gap-4 items-start p-6 rounded w-full">
            <div className="flex items-center gap-2 w-full">
              <TrendingUp className="h-4 w-4 text-[#020203] opacity-50" />
              <p className="font-mono font-medium leading-[1.5] opacity-50 text-[#020203] text-xs tracking-[0.6px] uppercase">
                Brand Mentions Over Time
              </p>
            </div>
            <MentionsChart data={dashboardData.mentionsTimeline} />
          </div>
        )}

        {providerAnalytics && providerAnalytics.timeline.length >= 2 && (
          <ProviderPerformanceChart
            timeline={providerAnalytics.timeline}
            providerSummaries={providerAnalytics.providerSummaries}
          />
        )}

        <div className="relative bg-white border border-[rgba(2,2,3,0.06)] flex flex-col gap-5 items-start p-6 rounded w-full">
          <div className="flex flex-col items-start w-full">
            <div className="flex flex-col items-start justify-center w-full">
              <div className="flex flex-col items-start w-full">
                <div className="flex items-start w-full">
                  <p className="flex-1 font-mono font-medium leading-[1.5] opacity-50 text-[#020203] text-xs tracking-[0.6px] uppercase">
                    Queries (Showing {displayQueries.length})
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* First query - always visible and accessible */}
          {displayQueries.length > 0 && (() => {
            const firstQuery = displayQueries[0];
            const { mentioned, notMentioned } = getQueryLLMs(firstQuery);
            return (
              <div className="flex flex-col gap-4 items-start w-full">
                <button
                  onClick={() => handleQueryClick(firstQuery)}
                  className="flex flex-col md:flex-row items-start justify-between w-full text-left transition-opacity hover:opacity-70 cursor-pointer gap-3 md:gap-4"
                >
                  <div className="flex-1 flex flex-col gap-1 items-start justify-center leading-[1.5] text-[#020203] text-base">
                    <p className="font-sans font-[450]" style={{ fontVariationSettings: "'wdth' 100" }}>
                      {firstQuery.query}
                    </p>
                    <div className="opacity-50 overflow-hidden w-full line-clamp-2">
                      <MarkdownRenderer content={firstQuery.response} className="text-sm [&>*]:mb-0 [&>*:last-child]:mb-0" />
                    </div>
                  </div>
                  <div className="flex gap-2 items-start w-full md:w-auto">
                    <div className="flex-1 flex flex-col gap-2 items-end justify-center">
                      <div className="flex gap-2 items-center justify-end w-full">
                        <ThumbsUp className="w-3 h-3 text-[#00a944]" strokeWidth={3} />
                        <span className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-[#00a944]">Mention</span>
                      </div>
                      <div className="flex h-6 items-center justify-end w-full">
                        {mentioned.map((llm) => (
                          <div key={llm} className="bg-white border border-[rgba(2,2,3,0.06)] rounded-full overflow-hidden size-6">
                            <div className="flex items-center justify-center size-full p-1">
                              <img
                                alt=""
                                className="max-w-none object-cover pointer-events-none size-full"
                                src={LLM_PROVIDER_LOGOS[llm]}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex-1 flex flex-col gap-2 items-end">
                      <div className="flex gap-2 items-center justify-end w-full">
                        <ThumbsDown className="w-3 h-3 text-[#e14944]" strokeWidth={3} />
                        <span className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-[#e14944]">No Mention</span>
                      </div>
                      <div className="flex h-6 items-center justify-end pr-1.5 w-full">
                        {notMentioned.map((llm, idx) => (
                          <div
                            key={llm}
                            className="bg-white border border-[rgba(2,2,3,0.06)] rounded-full overflow-hidden size-6 -mr-1.5"
                            style={{ zIndex: notMentioned.length - idx }}
                          >
                            <div className="flex items-center justify-center size-full p-0.5">
                              <img
                                alt=""
                                className="max-w-none object-cover pointer-events-none size-full"
                                src={LLM_PROVIDER_LOGOS[llm]}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </button>
                {displayQueries.length > 1 && (
                  <div className="bg-[rgba(2,2,3,0.06)] h-px w-full" />
                )}
              </div>
            );
          })()}

          {/* Remaining queries - paywalled for non-subscribers */}
          {displayQueries.length > 1 && (
            <div className="relative flex flex-col gap-4 items-start w-full">
              {displayQueries.slice(1).map((query, index) => {
                const { mentioned, notMentioned } = getQueryLLMs(query);
                return (
                  <React.Fragment key={query.id}>
                    <div className="flex flex-col gap-4 items-start justify-center w-full">
                      <button
                        onClick={() => isSubscribed && handleQueryClick(query)}
                        disabled={!isSubscribed}
                        className={`flex flex-col md:flex-row items-start justify-between w-full text-left transition-opacity gap-3 md:gap-4 ${
                          !isSubscribed
                            ? 'cursor-not-allowed opacity-40 select-none blur-[2px]'
                            : 'hover:opacity-70 cursor-pointer'
                        }`}
                      >
                        <div className="flex-1 flex flex-col gap-1 items-start justify-center leading-[1.5] text-[#020203] text-base">
                          <p className="font-sans font-[450]" style={{ fontVariationSettings: "'wdth' 100" }}>
                            {query.query}
                          </p>
                          <div className="opacity-50 overflow-hidden w-full line-clamp-2">
                            <MarkdownRenderer content={query.response} className="text-sm [&>*]:mb-0 [&>*:last-child]:mb-0" />
                          </div>
                        </div>
                        <div className="flex gap-2 items-start w-full md:w-auto">
                          <div className="flex-1 flex flex-col gap-2 items-end justify-center">
                            <div className="flex gap-2 items-center justify-end w-full">
                              <ThumbsUp className="w-3 h-3 text-[#00a944]" strokeWidth={3} />
                              <span className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-[#00a944]">Mention</span>
                            </div>
                            <div className="flex h-6 items-center justify-end w-full">
                              {mentioned.map((llm) => (
                                <div key={llm} className="bg-white border border-[rgba(2,2,3,0.06)] rounded-full overflow-hidden size-6">
                                  <div className="flex items-center justify-center size-full p-1">
                                    <img
                                      alt=""
                                      className="max-w-none object-cover pointer-events-none size-full"
                                      src={LLM_PROVIDER_LOGOS[llm]}
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = 'none';
                                      }}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="flex-1 flex flex-col gap-2 items-end">
                            <div className="flex gap-2 items-center justify-end w-full">
                              <ThumbsDown className="w-3 h-3 text-[#e14944]" strokeWidth={3} />
                              <span className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-[#e14944]">No Mention</span>
                            </div>
                            <div className="flex h-6 items-center justify-end pr-1.5 w-full">
                              {notMentioned.map((llm, idx) => (
                                <div
                                  key={llm}
                                  className="bg-white border border-[rgba(2,2,3,0.06)] rounded-full overflow-hidden size-6 -mr-1.5"
                                  style={{ zIndex: notMentioned.length - idx }}
                                >
                                  <div className="flex items-center justify-center size-full p-0.5">
                                    <img
                                      alt=""
                                      className="max-w-none object-cover pointer-events-none size-full"
                                      src={LLM_PROVIDER_LOGOS[llm]}
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = 'none';
                                      }}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </button>
                      {index < displayQueries.length - 2 && (
                        <div className="bg-[rgba(2,2,3,0.06)] h-px w-full" />
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
              <ResultsPaywallOverlay show={!isSubscribed} />
            </div>
          )}

          {isSubscribed && (
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-5 items-stretch w-full">
              <button
                onClick={() => navigate('/dashboard/queries')}
                className="flex-1 bg-white border border-[rgba(2,2,3,0.1)] flex gap-3 h-14 items-center justify-center px-3 py-2.5 rounded hover:opacity-80 transition-opacity"
              >
                <div className="flex items-center">
                  <p className="font-mono font-medium leading-[1.5] text-[#020203] text-center text-sm tracking-[0.7px] uppercase">
                    View all queries
                  </p>
                </div>
              </button>
              <button
                onClick={() => navigate('/')}
                className="flex-1 bg-white border border-[rgba(2,2,3,0.1)] flex gap-3 h-14 items-center justify-center px-3 py-2.5 rounded hover:opacity-80 transition-opacity"
              >
                <div className="flex items-center">
                  <p className="font-mono font-medium leading-[1.5] text-[#020203] text-center text-sm tracking-[0.7px] uppercase">
                    + Add a query
                  </p>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {selectedQuery && (
        <QueryDetailModal
          open={showDetailModal}
          onOpenChange={setShowDetailModal}
          query={selectedQuery.query}
          responses={selectedQuery.llmResults.map(result => {
            const timestamp = new Date(selectedQuery.timestamp).toLocaleString('en-GB', {
              day: 'numeric',
              month: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }).replace(',', ' •');

            return {
              llm: result.llm,
              llmIcon: LLM_PROVIDER_LOGOS[result.llm],
              response: result.responseText || '',
              mentioned: result.mentioned ?? false,
              timestamp,
            };
          })}
        />
      )}
    </div>
  );
};

const DashboardPage: React.FC = () => {
  return (
    <div className="flex flex-col items-center w-full">
      <ErrorBoundary>
        <Routes>
          <Route index element={<Navigate to="/dashboard/insights" replace />} />
          <Route path="insights" element={<DashboardInsightsPage />} />
          <Route path="llms-txt" element={<DashboardLlmsTxtPage />} />
          <Route path="queries" element={<DashboardQueriesPage />} />
        </Routes>
      </ErrorBoundary>
    </div>
  );
};

export default DashboardPage;
